import type { Idea } from './ProductCard';

// Produit renvoye par GET /api/catalogue.
export interface ApiProduct {
  id: string;
  title: string;
  imageUrl: string;
  priceCents: number | null;
  currency: string;
  productUrl: string;
  partnerKey: string;
  // Vitrine (catalogue filtre par univers). Absents sur /coup-de-coeur.
  sponsored?: boolean;
  partnerSlug?: string | null;
  partnerProductId?: string | null;
  // Univers de la Vitrine (statistiques partenaire v2), avec sponsored.
  sponsorUniverse?: string | null;
  // Marque avec une option en cours (priorite 100 du miroir) : mention
  // « Sponsorisé » dans tous les univers. Absent sur /coup-de-coeur.
  promoted?: boolean;
  // Nom du partenaire (meme source que le cadeau ajoute).
  merchantName?: string | null;
}

// Valeur de ?selection= : les coups de cœur de la semaine (premier raccourci
// d'Idées cadeaux, cf. API listCatalogue).
export const COUPS_DE_COEUR = 'coups-de-coeur';

// Sous-ensemble des params /api/catalogue couverts par ce module. La page
// Idées cadeaux garde son propre builder pour ses filtres riches (univers,
// subcat, sort, marque legacy) ; ce type sert aux consommateurs simples.
export interface CatalogueQuery {
  page?: number;
  pageSize?: number;
  mif?: boolean;
  priceMin?: number;
  priceMax?: number;
  marques?: string[];
  q?: string;
}

export interface CataloguePage {
  ideas: Idea[];
  page: number;
  totalCount: number;
  totalPages: number;
}

// Donnees de seed inlinees dans le HTML prerendu (cf scripts/prerender.mjs) :
// la page Idées cadeaux expose window.__IC__ apres son premier fetch, le
// prerendu le serialise dans <script type="application/json" id="ic-seed">, et
// le client le relit (readIcSeed) comme etat initial pour eviter le collapse
// d'hydratation (contenu prerendu vide puis re-rempli = CLS/LCP degrades).
export interface IcSeed {
  // Vue par defaut d'Idées cadeaux (page 1, sans filtre ni recherche).
  cataloguePage?: { products: ApiProduct[]; totalCount: number; totalPages: number };
}
declare global {
  interface Window {
    __IC__?: IcSeed;
  }
}

let seedCache: IcSeed | undefined;

/** Seed du HTML prerendu, lu une seule fois. {} hors prerendu (navigation SPA). */
export function readIcSeed(): IcSeed {
  if (seedCache) return seedCache;
  const text = typeof document !== 'undefined' ? document.getElementById('ic-seed')?.textContent : null;
  try {
    seedCache = text ? (JSON.parse(text) as IcSeed) : {};
  } catch {
    seedCache = {};
  }
  return seedCache;
}

// Mappe un produit API vers le type Idea attendu par ProductCard. Tous les
// produits /api/catalogue proviennent des flux d'affiliation.
export function apiProductToIdea(p: ApiProduct, partnerNameByKey: Record<string, string>): Idea {
  return {
    id: p.id,
    title: p.title,
    imageUrl: p.imageUrl,
    priceCents: p.priceCents,
    currency: p.currency,
    productUrl: p.productUrl,
    partnerKey: p.partnerKey,
    source: 'EFFINITY_FEED',
    partnerLabel: partnerNameByKey[p.partnerKey] ?? p.merchantName ?? undefined,
    ...(p.sponsored && p.partnerSlug ? { sponsor: { slug: p.partnerSlug, productId: p.partnerProductId ?? undefined, target: p.sponsorUniverse ?? undefined } } : {}),
    ...(p.promoted ? { promoted: true } : {}),
  };
}

export function buildCatalogueUrl(query: CatalogueQuery): string {
  const params = new URLSearchParams();
  if (query.page != null) params.set('page', String(query.page));
  if (query.pageSize != null) params.set('pageSize', String(query.pageSize));
  if (query.mif) params.set('mif', '1');
  if (query.priceMin != null) params.set('priceMin', String(query.priceMin));
  if (query.priceMax != null) params.set('priceMax', String(query.priceMax));
  if (query.marques && query.marques.length === 1) params.set('marque', query.marques[0]);
  else if (query.marques && query.marques.length > 1) params.set('marques', query.marques.join(','));
  if (query.q && query.q.trim().length >= 2) params.set('q', query.q.trim());
  return `/api/catalogue?${params.toString()}`;
}

// GET /api/catalogue/partners -> map partnerKey -> nom de marque (badge carte).
export async function fetchCataloguePartners(): Promise<Record<string, string>> {
  try {
    const r = await fetch('/api/catalogue/partners');
    if (!r.ok) return {};
    const data: { partners?: { key: string; name: string }[] } = await r.json();
    return Object.fromEntries((data.partners ?? []).map(p => [p.key, p.name]));
  } catch {
    return {};
  }
}

export async function fetchCataloguePage(query: CatalogueQuery, partnerNameByKey: Record<string, string>): Promise<CataloguePage> {
  const r = await fetch(buildCatalogueUrl(query));
  if (!r.ok) throw new Error('catalogue fetch failed');
  const data: { products: ApiProduct[]; pagination: { page: number; totalCount: number; totalPages: number } } = await r.json();
  return {
    ideas: data.products.map(p => apiProductToIdea(p, partnerNameByKey)),
    page: data.pagination.page,
    totalCount: data.pagination.totalCount,
    totalPages: data.pagination.totalPages,
  };
}
